import { createContext, useContext, useState, useCallback } from 'react';

import { SnackbarContainer } from '../../components/snackbar/SnackbarContainer';

import type {
  SnackbarContextValue,
  SnackbarMessage,
  ShowSnackbarArgs,
  ShowPromiseSnackbarArgs,
  SnackbarProviderProps,
} from './types';

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export function SnackbarProvider({
  children,
  maxSnackbars = 5,
  autoHideDuration = 3000,
  position = 'top-center',
}: SnackbarProviderProps) {
  const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([]);

  const generateId = () => `snackbar-${Date.now()}-${Math.random()}`;

  const showSnackbar = useCallback(
    ({
      message,
      variant = 'default',
      autoHideDuration: customDuration,
      action,
    }: ShowSnackbarArgs): string => {
      const id = generateId();

      setSnackbars((prev) => {
        const newSnackbars = [
          ...prev,
          {
            id,
            message,
            variant,
            autoHideDuration: customDuration === undefined ? autoHideDuration : customDuration,
            action,
          },
        ];

        if (newSnackbars.length > maxSnackbars) {
          return newSnackbars.slice(-maxSnackbars);
        }

        return newSnackbars;
      });

      return id;
    },
    [autoHideDuration, maxSnackbars],
  );

  const hideSnackbar = useCallback((id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  }, []);

  const showPromiseSnackbar = useCallback(
    ({ message, promise, autoHideDuration: customDuration }: ShowPromiseSnackbarArgs): string => {
      const id = generateId();

      setSnackbars((prev) => [
        ...prev,
        {
          id,
          message: message.pending,
          variant: 'info',
          pending: true,
          autoHideDuration: null,
        },
      ]);

      promise()
        .then(() => {
          setSnackbars((prev) =>
            prev.map((snackbar) =>
              snackbar.id === id
                ? {
                    ...snackbar,
                    message: message.success,
                    variant: 'success' as const,
                    pending: false,
                    autoHideDuration:
                      customDuration === undefined ? autoHideDuration : customDuration,
                  }
                : snackbar,
            ),
          );
        })
        .catch(() => {
          setSnackbars((prev) =>
            prev.map((snackbar) =>
              snackbar.id === id
                ? {
                    ...snackbar,
                    message: message.error,
                    variant: 'error' as const,
                    pending: false,
                    autoHideDuration:
                      customDuration === undefined ? autoHideDuration : customDuration,
                  }
                : snackbar,
            ),
          );
        });

      return id;
    },
    [autoHideDuration, maxSnackbars],
  );

  const value: SnackbarContextValue = {
    showSnackbar,
    hideSnackbar,
    showPromiseSnackbar,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <SnackbarContainer snackbars={snackbars} onClose={hideSnackbar} position={position} />
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarContextValue {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}
